import ServiceUnavailableAlert from 'components/core/ServiceUnavailableAlert';
import useIsMobile from 'hooks/useIsMobile';
import ModalProntuario from 'pages/laudo/prontuario/ModalProntuario';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, ListGroup, Row, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectDoctor } from 'redux/features/auth/authSlice';
import {
  createAtestado,
  deleteAtestado,
  getAtestadosPaciente,
  updateAtestado,
} from 'services/atestado';
import useSWR from 'swr';
import { isInfrastructureError } from 'utils/apiErrors';
import { enviarArquivo, toWhatsappNumber } from 'utils/evolution';
import { mensagemErroExclusao, podeExcluirDocumento } from 'utils/gravacao';
import {
  formatarDataExtenso,
  formatarDataNascimento,
  gerarTextoAtestado,
  getAtestadoById,
  getTiposAtestado,
  mascaraCPF,
} from 'utils/helpers';
import { blobToBase64 } from 'utils/pdfBlob';
import { generateAtestadoPdf } from './generateAtestadoPdf';

export default function Atestado({ prontuario }) {
  const history = useHistory();
  const isMobile = useIsMobile();
  const medico = useSelector(selectDoctor);
  const canMutate = !!medico;
  const codPac = prontuario?.CODPAC;
  const swrKey = codPac ? ['atestados-paciente', codPac] : null;
  const [codAtestadoSelecionado, setCodAtestadoSelecionado] = useState(undefined);
  const [descricao, setDescricao] = useState('');
  const [tipoAtestadoId, setTipoAtestadoId] = useState(1);
  const [modoNovoAtestado, setModoNovoAtestado] = useState(false);
  const [showModalProntuario, setShowModalProntuario] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [dadosConferidos, setDadosConferidos] = useState(false);
  const tiposAtestado = getTiposAtestado();

  const {
    data: atestados = [],
    error,
    isLoading,
    mutate,
  } = useSWR(swrKey, ([, cod]) => getAtestadosPaciente(cod), {
    shouldRetryOnError: false,
  });

  const loading = isLoading || mutating;
  const atestadoSelecionado = useMemo(() => {
    if (!atestados.length) return null;
    if (codAtestadoSelecionado === null) return null;
    if (codAtestadoSelecionado === undefined) return atestados[0];
    return atestados.find((item) => item.CODATESTADO === codAtestadoSelecionado) || atestados[0];
  }, [atestados, codAtestadoSelecionado]);
  const semAtestados = atestados.length === 0;
  const temAtestados = atestados.length > 0;
  const erroInfraestrutura = isInfrastructureError(error);
  const exigeConferenciaDados = !loading && semAtestados && canMutate;
  const isDefinitivo = atestadoSelecionado?.GRAVACAO === 'DEFINITIVO';
  const podeExcluir =
    canMutate && atestadoSelecionado && podeExcluirDocumento(atestadoSelecionado.GRAVACAO);
  const isEditLocked = isDefinitivo || !canMutate;
  const podeSelecionarTipoAtestado = modoNovoAtestado && !loading && !isEditLocked;
  const dadosBasicosPaciente = {
    cpf: mascaraCPF(prontuario?.CPFPAC || prontuario?.CPF),
    nomeCompleto: prontuario?.NOMEPAC || '-',
    dataNascimento: formatarDataNascimento(prontuario),
    celular: prontuario?.CELULARPAC || prontuario?.celular || '-',
  };

  useEffect(() => {
    if (atestadoSelecionado) {
      setDescricao(atestadoSelecionado.DESCRICAO || '');
      const tipoEncontrado = tiposAtestado.find((t) => t.tipo === atestadoSelecionado.TIPOATESTADO);
      setTipoAtestadoId(tipoEncontrado?.id || 1);
    }
  }, [atestadoSelecionado?.CODATESTADO, tiposAtestado]);

  // Gera o texto do atestado quando muda tipo ou campos extras
  const gerarTexto = useCallback(() => {
    const tipoAtestado = getAtestadoById(tipoAtestadoId);
    if (!tipoAtestado || !prontuario) return;

    const dados = {
      nomePaciente: prontuario.NOMEPAC || '',
      rg: prontuario.RGPAC || prontuario.RG || '',
      dataExtenso: formatarDataExtenso(new Date()),
      horaInicio: '--:00',
      horaFim: '--:00',
      dias: '____',
      cid: '',
    };

    const texto = gerarTextoAtestado(tipoAtestadoId, dados);
    setDescricao(texto);
  }, [tipoAtestadoId, prontuario]);

  // Gera texto automatico no fluxo de novo atestado.
  // Quando o usuario navega por atestados existentes, mantemos o texto salvo no banco.
  useEffect(() => {
    if (modoNovoAtestado || !atestadoSelecionado) {
      gerarTexto();
    }
  }, [gerarTexto, atestadoSelecionado, modoNovoAtestado]);

  const selecionarUltimoAtestado = (lista) => {
    if (Array.isArray(lista) && lista.length > 0) {
      setCodAtestadoSelecionado(lista[0].CODATESTADO);
    }
  };

  const handleNovoAtestado = async () => {
    if (!canMutate) {
      toast.warn('Apenas médicos podem criar atestados');
      return;
    }
    if (!prontuario || !medico) {
      toast.warn('Dados do paciente ou médico não disponíveis');
      return;
    }

    if (semAtestados && !dadosConferidos) {
      toast.warn('Confirme os dados básicos do paciente para criar o primeiro atestado');
      return;
    }

    // Resetar campos para novo atestado (mantém o tipo selecionado)
    setModoNovoAtestado(true);
    setCodAtestadoSelecionado(null);

    // Gerar texto do template selecionado pelo usuário
    const tipoAtestado = getAtestadoById(tipoAtestadoId);
    if (!tipoAtestado) return;

    const dados = {
      nomePaciente: prontuario.NOMEPAC || '',
      rg: prontuario.RGPAC || prontuario.RG || '',
      dataExtenso: formatarDataExtenso(new Date()),
    };

    const textoInicial = gerarTextoAtestado(tipoAtestadoId, dados);

    const payload = {
      codConsulta: prontuario?.CODCONSULTA ?? null,
      codMedico: medico.codMed,
      codPac: prontuario.CODPAC,
      tipoAtestado: tipoAtestado.tipo,
      descricao: textoInicial,
      // cid: null,
      // dias: null,
      status: 'ONLINE',
    };

    try {
      setMutating(true);
      await createAtestado(payload);
      toast.success('Novo atestado criado com sucesso!');
      const updatedData = await mutate();
      selecionarUltimoAtestado(updatedData);
      // setModoNovoAtestado(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao criar novo atestado');
    } finally {
      setMutating(false);
    }
  };

  // usar swr pra mutation???
  const handleRepetirAtestado = async () => {
    if (!canMutate || !medico) {
      toast.warn('Apenas médicos podem repetir atestados');
      return;
    }
    if (!atestadoSelecionado) {
      toast.warn('Selecione um atestado para repetir');
      return;
    }

    setModoNovoAtestado(false);

    // Atualizar a data no texto
    const dataExtenso = formatarDataExtenso(new Date());
    const textoAtualizado = (atestadoSelecionado.DESCRICAO || '').replace(
      /\d{1,2} de \w+ de \d{4}/g,
      dataExtenso
    );

    const payload = {
      codConsulta: prontuario?.CODCONSULTA ?? null,
      codMedico: medico.codMed,
      codPac: prontuario.CODPAC,
      tipoAtestado: atestadoSelecionado.TIPOATESTADO,
      descricao: textoAtualizado,
      status: 'ONLINE',
    };

    try {
      setMutating(true);
      await createAtestado(payload);
      toast.success('Atestado repetido com sucesso!');
      const updatedData = await mutate();
      selecionarUltimoAtestado(updatedData);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao repetir atestado');
    } finally {
      setMutating(false);
    }
  };

  // TODO: swr para mutation
  const handleGravar = async (gravacao) => {
    if (!canMutate || !medico) {
      toast.warn('Apenas médicos podem gravar atestados');
      return;
    }
    if (!atestadoSelecionado) {
      toast.warn('Selecione um atestado para gravar!');
      return;
    }

    try {
      // Ao gravar, paramos a geração automática de template para preservar o texto editado.
      setModoNovoAtestado(false);

      const tipoAtestadoSelecionado = getAtestadoById(tipoAtestadoId);
      const codAtual = atestadoSelecionado.CODATESTADO;

      const payload = {
        tipoAtestado: tipoAtestadoSelecionado?.tipo || '',
        descricao,
        gravacao,
      };
      setMutating(true);
      await updateAtestado(codAtual, payload);
      toast.success('Atestado atualizado com sucesso!');
      await mutate(
        (prev = []) =>
          Array.isArray(prev)
            ? prev.map((item) =>
                item.CODATESTADO === codAtual
                  ? { ...item, DESCRICAO: descricao, GRAVACAO: gravacao }
                  : item
              )
            : prev,
        { revalidate: true }
      );
      setCodAtestadoSelecionado(codAtual);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao gravar atestado');
    } finally {
      setMutating(false);
    }
  };

  const handleExcluir = async () => {
    if (!canMutate || !medico) {
      toast.warn('Apenas médicos podem excluir atestados');
      return;
    }
    if (!atestadoSelecionado || !podeExcluirDocumento(atestadoSelecionado.GRAVACAO)) {
      toast.warn('Atestado definitivo não pode ser excluído');
      return;
    }

    const confirmar = window.confirm(
      `Excluir o atestado #${atestadoSelecionado.CODATESTADO}? Esta ação não pode ser desfeita.`
    );
    if (!confirmar) return;

    const cod = atestadoSelecionado.CODATESTADO;
    try {
      setMutating(true);
      await deleteAtestado(cod);
      const restantes = await mutate(
        (prev = []) =>
          Array.isArray(prev) ? prev.filter((item) => item.CODATESTADO !== cod) : prev,
        { revalidate: true }
      );
      setCodAtestadoSelecionado(
        Array.isArray(restantes) && restantes[0] ? restantes[0].CODATESTADO : null
      );
      toast.success('Atestado excluído com sucesso.');
    } catch (err) {
      toast.error(mensagemErroExclusao(err, 'Erro ao excluir atestado'));
    } finally {
      setMutating(false);
    }
  };

  const handleImprimir = () => {
    if (!descricao.trim()) {
      toast.warn('O texto do atestado não pode estar vazio');
      return;
    }

    history.push({
      pathname: '/imprimir/atestado',
      state: {
        atestado: {
          ...(atestadoSelecionado || {}),
          DESCRICAO: descricao,
          TIPOATESTADO: tipoAtestadoId,
          // CID: cid,
          // DIAS: dias,
        },
        prontuario,
        medico,
      },
    });
  };

  async function handleEnviarWhatsapp() {
    if (!atestadoSelecionado || !descricao.trim()) {
      toast.warn('Selecione e grave um atestado antes de enviar');
      return;
    }

    try {
      const celularWhats = toWhatsappNumber(
        atestadoSelecionado?.CELULARPAC || prontuario?.CELULARPAC
      );

      const atestadoParaEnviar = {
        ...atestadoSelecionado,
        DESCRICAO: descricao,
        TIPOATESTADO: tipoAtestadoId,
      };

      const blob = await generateAtestadoPdf({
        atestado: atestadoParaEnviar,
        prontuario,
      });

      const pdfBase64 = await blobToBase64(blob);

      const payload = {
        to: celularWhats,
        pdfBase64,
        paciente: {
          id: atestadoSelecionado?.CODPAC || prontuario?.CODPAC,
          nome: atestadoSelecionado?.NOMEPAC || prontuario?.NOMEPAC,
          medico: medico?.apelido,
        },
        codLaudo: atestadoSelecionado?.CODATESTADO,
        fileName: `Atestado_${atestadoSelecionado?.CODATESTADO}.pdf`,
        caption: `Ola *${atestadoSelecionado?.NOMEPAC || prontuario?.NOMEPAC || 'Paciente'}*, tudo bem?\n\nSegue em anexo o seu *Atestado Médico*.\n\nQualquer duvida, nossa equipe estará à disposição no setor de atendimento: (14) 3402-2700`,
      };

      const result = await enviarArquivo(payload);

      toast.success(result.message);
    } catch (error) {
      // colocar Sentry aqui também!
      toast.error(error?.message || 'Erro ao enviar por WhatsApp.');
    }
  }

  return (
    <>
      {loading && (
        <div className="text-center mb-2">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {error &&
        (erroInfraestrutura ? (
          <ServiceUnavailableAlert error={error} stale={temAtestados} onRetry={() => mutate()} />
        ) : (
          <Alert variant="danger">
            <p className="mb-0">{error?.response?.data?.message || 'Erro ao carregar atestados'}</p>
            <Button variant="outline-danger" size="sm" className="mt-3" onClick={() => mutate()}>
              Tentar novamente
            </Button>
          </Alert>
        ))}

      {(!error || temAtestados) && (
        <Row>
          {/* TODO: controlar isso por paginação do backend e não maxHeight e overflowY */}
          <Col md={2} className="mb-3 mb-md-0">
            <ListGroup style={{ maxHeight: isMobile ? '215px' : '700px', overflowY: 'auto' }}>
              {atestados.map((atestado) => (
                <ListGroup.Item
                  key={atestado.CODATESTADO}
                  active={atestadoSelecionado?.CODATESTADO === atestado.CODATESTADO}
                  onClick={() => {
                    setModoNovoAtestado(false);
                    setCodAtestadoSelecionado(atestado.CODATESTADO);
                  }}
                  style={{
                    cursor: 'pointer',
                    background:
                      atestadoSelecionado?.CODATESTADO === atestado.CODATESTADO
                        ? '#0d6efd'
                        : '#fff',
                    color:
                      atestadoSelecionado?.CODATESTADO === atestado.CODATESTADO
                        ? '#fff'
                        : '#212529',
                    borderColor:
                      atestadoSelecionado?.CODATESTADO === atestado.CODATESTADO
                        ? '#0d6efd'
                        : '#dee2e6',
                    boxShadow:
                      atestadoSelecionado?.CODATESTADO === atestado.CODATESTADO
                        ? '0 0 8px #0d6efd'
                        : '0 0 4px #dee2e6',
                    transition: 'background 0.2s, border-color 0.2s, color 0.2s',
                  }}
                  onMouseOver={(e) => {
                    if (
                      !atestadoSelecionado ||
                      atestadoSelecionado?.CODATESTADO !== atestado.CODATESTADO
                    ) {
                      e.currentTarget.style.background = '#e9ecef';
                      e.currentTarget.style.borderColor = '#0d6efd';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (
                      !atestadoSelecionado ||
                      atestadoSelecionado?.CODATESTADO !== atestado.CODATESTADO
                    ) {
                      e.currentTarget.style.background = '#fff';
                      e.currentTarget.style.borderColor = '#dee2e6';
                    }
                  }}
                >
                  <div className="d-flex flex-column">
                    <strong>{atestado.dataFormatada}</strong>
                    <small className="text-muted">
                      {tiposAtestado.find((t) => t.tipo === atestado.TIPOATESTADO)?.tipo ||
                        'Atestado'}
                    </small>
                  </div>
                </ListGroup.Item>
              ))}
              {atestados.length === 0 && !loading && (
                <div className="text-center text-muted py-4">
                  <p>Nenhum atestado encontrado</p>
                </div>
              )}
            </ListGroup>
          </Col>

          {/* Coluna Direita: Editor e Opções */}
          <Col md={10}>
            <Row
              style={{
                pointerEvents: isEditLocked ? 'none' : 'auto',
                opacity: isEditLocked ? 0.6 : 1,
              }}
            >
              <Col md={6}>
                <Form.Label className="font-weight-bold">
                  Paciente: {prontuario?.NOMEPAC || ''}
                </Form.Label>
              </Col>
              <Col md={6}>
                <Form.Label className="font-weight-bold">
                  Médico: {atestadoSelecionado?.NOMEMED || medico?.nome || ''}
                </Form.Label>
              </Col>
            </Row>

            {exigeConferenciaDados && (
              <Alert variant="info" className="mb-3">
                <Alert.Heading className="h6 mb-2">
                  Nenhum atestado encontrado para este paciente
                </Alert.Heading>
                <p className="mb-2">
                  Confira os dados básicos abaixo antes de inserir o primeiro atestado.
                </p>
                <Row className="mb-2">
                  <Col md={6}>
                    <strong>CPF:</strong> {dadosBasicosPaciente.cpf}
                  </Col>
                  <Col md={6}>
                    <strong>Nome completo:</strong> {dadosBasicosPaciente.nomeCompleto}
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col md={6}>
                    <strong>Data de nascimento:</strong> {dadosBasicosPaciente.dataNascimento}
                  </Col>
                  <Col md={6}>
                    <strong>Celular:</strong> {dadosBasicosPaciente.celular}
                  </Col>
                </Row>
                <Form.Check
                  id="confirmar-dados-primeiro-atestado"
                  type="checkbox"
                  label="Confirmo que os dados básicos do paciente foram conferidos"
                  checked={dadosConferidos}
                  onChange={(e) => setDadosConferidos(e.target.checked)}
                />
              </Alert>
            )}

            <div className="mb-2">
              <div
                style={{
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '10px',
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                  gap: isMobile ? '0.35rem' : undefined,
                  opacity: isEditLocked ? 0.6 : 1,
                  pointerEvents: isEditLocked ? 'none' : 'auto',
                }}
              >
                {tiposAtestado.map((tipo) => (
                  <Form.Check
                    key={tipo.id}
                    type="radio"
                    id={`tipo-${tipo.id}`}
                    name="tipoAtestado"
                    label={tipo.tipo}
                    checked={tipoAtestadoId === tipo.id}
                    onChange={() => setTipoAtestadoId(tipo.id)}
                    style={{ fontSize: '13px' }}
                    disabled={!podeSelecionarTipoAtestado}
                  />
                ))}
              </div>
              <small className="text-muted d-block mt-1">
                **O tipo de atestado só pode ser alterado ao clicar em Novo Atestado**
              </small>
            </div>

            <Form.Group className="mb-2">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <Button
                  className="w-100 w-md-auto"
                  onClick={handleNovoAtestado}
                  disabled={loading || !canMutate || (semAtestados && !dadosConferidos)}
                  size="sm"
                >
                  Novo Atestado
                </Button>
                <Button
                  className="w-100 w-md-auto"
                  variant="info"
                  size="sm"
                  onClick={handleRepetirAtestado}
                  disabled={loading || !atestadoSelecionado || !canMutate}
                >
                  Repetir Atestado
                </Button>
                <Button
                  className="w-100 w-md-auto"
                  variant="success"
                  onClick={() => setShowModalProntuario(true)}
                  disabled={loading || !prontuario}
                  size="sm"
                >
                  Anamnese
                </Button>
                <strong>Descrição</strong>
                {atestadoSelecionado && (
                  <>
                    <small className="text-muted ms-2">
                      (Atestado {atestadoSelecionado.CODATESTADO} -{' '}
                      {atestadoSelecionado.dataFormatada})
                    </small>
                    <Badge bg={isDefinitivo ? 'success' : 'warning'}>
                      {isDefinitivo ? 'DEFINITIVO' : 'PARCIAL'}
                    </Badge>
                  </>
                )}
              </div>
              <Form.Control
                as="textarea"
                rows={isMobile ? 10 : 15}
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="O texto do atestado será gerado automaticamente ao selecionar o tipo"
                style={{
                  fontSize: '14px',
                  lineHeight: '1.5',
                  resize: 'vertical',
                }}
                disabled={isEditLocked}
              />
            </Form.Group>

            <div className="d-flex flex-wrap justify-content-center gap-2">
              <Button
                className="w-100 w-md-auto"
                variant="outline-success"
                onClick={() => handleGravar('PARCIAL')}
                disabled={loading || !atestadoSelecionado || isDefinitivo || !canMutate}
              >
                Gravar Parcial
              </Button>
              <Button
                className="w-100 w-md-auto"
                variant="outline-success"
                onClick={() => handleGravar('DEFINITIVO')}
                disabled={loading || !atestadoSelecionado || isDefinitivo || !canMutate}
              >
                Gravar Definitivo
              </Button>
              {podeExcluir && (
                <Button
                  className="w-100 w-md-auto"
                  variant="outline-danger"
                  onClick={handleExcluir}
                  disabled={loading}
                >
                  Excluir
                </Button>
              )}
              <Button
                className="w-100 w-md-auto"
                variant="warning"
                onClick={() => handleImprimir()}
                disabled={!atestadoSelecionado || !descricao.trim() || !isDefinitivo}
              >
                Timbrado
              </Button>
              <Button
                className="w-100 w-md-auto"
                onClick={handleEnviarWhatsapp}
                disabled={!atestadoSelecionado || !descricao.trim() || !isDefinitivo}
                variant="outline-success"
              >
                <i className="bi bi-whatsapp" style={{ color: '#25d366', marginRight: 6 }} /> Enviar
              </Button>
            </div>
          </Col>
        </Row>
      )}

      <ModalProntuario
        show={showModalProntuario}
        onHide={() => setShowModalProntuario(false)}
        prontuario={prontuario}
      />
    </>
  );
}
